import { ReactNode } from "react";
import { Alert, Button, Space, theme, Typography } from "antd";
import {
  TableOutlined,
  BarChartOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { ContentView, ContentViews } from "./useContentView";

const { Text } = Typography;

export function InvalidAggregations({
  minAggregations = 1,
  maxAggregations = 1,
  numAggregations = 1,
  view,
}: {
  minAggregations?: number;
  maxAggregations?: number;
  numAggregations?: number;
  view?: ContentView;
}) {
  const { token } = theme.useToken();
  const tooFewAggregations = numAggregations < minAggregations;
  const tooManyAggregations =
    maxAggregations !== undefined && numAggregations > maxAggregations;

  const getStatusMessage = () => {
    if (tooFewAggregations) {
      return {
        title: "Visualization Limit",
        subtitle: `Minimum ${minAggregations} measures required`,
        type: "error" as const,
      };
    }
    if (tooManyAggregations) {
      return {
        title: "Visualization Limit",
        subtitle: `Maximum ${maxAggregations} groups exceeded.`,
        type: "error" as const,
      };
    }
    return null;
  };

  const status = getStatusMessage();
  if (!status) return null;

  return (
    <div
      style={{
        padding: "16px",
        maxWidth: 600,
        margin: "0 auto",
        background: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Header */}
        <div>
          <Text
            type="secondary"
            style={{
              fontSize: 13,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "block",
              marginBottom: 8,
            }}
          >
            {status.title}
          </Text>
          <Text strong style={{ fontSize: 20 }}>
            {status.subtitle}
          </Text>
        </div>

        {/* Context */}
        <div
          style={{
            background: token.colorInfoBg,
            padding: "12px 16px",
            borderRadius: token.borderRadius,
            border: `1px solid ${token.colorInfoBorder}`,
          }}
        >
          <Space align="start">
            <Text type="secondary" style={{ fontSize: 24, lineHeight: 1 }}>
              {numAggregations}
            </Text>
            <div>
              <Text>measures selected</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                This visualization works with {minAggregations} and{" "}
                {maxAggregations} measures
              </Text>
            </div>
          </Space>
        </div>

        {/* Solutions */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 12 }}>
            Modify the query to meet the requirements
          </Text>
          <Space>
            <Typography.Text>
              Or you can switch to the <b>Table View</b> to see the data with
              the current query.
            </Typography.Text>
            <Button
              icon={<TableOutlined />}
              onClick={() => {
                if (view) {
                  view.set(ContentViews.dataTable);
                }
              }}
            >
              View as Table
            </Button>
          </Space>
        </div>
      </Space>
    </div>
  );
}
